import { api } from "services/api";
import type {
  ClassSession,
  LeaveRequestResponse,
  PaginatedResponse,
  SessionParticipant,
} from "types/domain";

export interface LearningCourseItem {
  class_id: string;
  class_code: string | null;
  course_title: string;
  course_description: string | null;
  total_sessions: number | null;
  remaining_sessions: number | null;
  center_name: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  instructor_name: string | null;
  instructor_avatar_url: string | null;
  instructor_bio: string | null;
  instructor_achievement: string | null;
  instructor_certificate: string | null;
}

export interface LearningSessionItem {
  session_id: string;
  class_id: string | null;
  class_code: string | null;
  title: string;
  status: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  // Populated by the backend from session.venue.name — optional/defensive
  // because it's being added in parallel and may not be deployed yet.
  venue_name?: string | null;
}

export interface MeLearningResponse {
  courses: LearningCourseItem[];
  upcoming_sessions: LearningSessionItem[];
  past_sessions: LearningSessionItem[];
}

export interface MeSettingsProfile {
  full_name: string | null;
  email: string | null;
  username: string | null;
  phone: string | null;
  role: string;
  center_name: string | null;
  avatar_url: string | null;
  dob: string | null;
  gender: string | null;
  height: number | null;
  weight: number | null;
  certificate: string | null;
  achievement: string | null;
  bio: string | null;
  base_salary: number | null;
  parent_phone: string | null;
  medical_note: string | null;
}

export interface MeSettingsProfileUpdate {
  full_name?: string;
  email?: string;
  username?: string;
  phone?: string;
  avatar_url?: string | null;
  dob?: string | null;
  gender?: string | null;
  height?: number | null;
  weight?: number | null;
  certificate?: string | null;
  achievement?: string | null;
  bio?: string | null;
  base_salary?: number | null;
  parent_phone?: string | null;
  medical_note?: string | null;
}

async function uploadAvatar(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const data = await api.upload<{ avatar_url: string }>(
    "/users/me/avatar",
    formData
  );
  return data.avatar_url;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

/**
 * Mirrors the backend attendance response. `status` is optional/defensive
 * because a backend teammate is adding it in parallel and it may not be
 * present in every deployed API version yet. Likewise
 * `check_in_snapshot_url` / `check_out_snapshot_url` are being added by a
 * companion backend change (presigned photo URLs) and may not be present
 * in every deployed API version yet. Likewise `class_name`, `session_title`,
 * `session_start_time`, `session_number` and `venue_name` are being added
 * by a backend teammate in parallel (class/session/venue context for the
 * student-facing attendance history page) and may not be present in every
 * deployed API version yet.
 */
export interface AttendanceResponse {
  id: string;
  session_id: string | null;
  checked_in_at: string | null;
  checked_out_at: string | null;
  check_in_method: string | null;
  check_out_method?: string | null;
  check_in_location?: string | null;
  checkout_location?: string | null;
  check_in_snapshot_url?: string | null;
  check_out_snapshot_url?: string | null;
  notes?: string | null;
  status?: string | null;
  class_name?: string | null;
  session_title?: string | null;
  session_start_time?: string | null;
  session_number?: number | null;
  venue_name?: string | null;
  user?: {
    full_name: string;
    email: string;
  };
}

export const userPortalService = {
  getMyLearning: (): Promise<MeLearningResponse> =>
    api.get("/users/me/learning"),
  getMySettingsProfile: (): Promise<MeSettingsProfile> =>
    api.get("/users/me/settings-profile"),
  updateMySettingsProfile: (
    payload: MeSettingsProfileUpdate
  ): Promise<MeSettingsProfile> =>
    api.patch("/users/me/settings-profile", payload),
  changePassword: (
    userId: string,
    payload: ChangePasswordPayload
  ): Promise<unknown> => api.post(`/users/${userId}/change-password`, payload),
  uploadAvatar,
  getMyAttendance: (params?: {
    skip?: number;
    limit?: number;
    session_id?: string;
  }): Promise<PaginatedResponse<AttendanceResponse>> => {
    const skip = params?.skip ?? 0;
    const limit = params?.limit ?? 20;
    const sessionIdQuery = params?.session_id
      ? `&session_id=${params.session_id}`
      : "";
    return api.get(
      `/attendance/my?skip=${skip}&limit=${limit}${sessionIdQuery}`
    );
  },
  getMySessionsByDateRange: (
    userId: string,
    from: string,
    to: string,
    skip = 0,
    limit = 100
  ): Promise<PaginatedResponse<ClassSession>> =>
    api.get(
      `/users/${userId}/sessions?from_datetime=${from}&to_datetime=${to}&skip=${skip}&limit=${limit}`
    ),
  // Session detail for STUDENT: the backend is expected to scope this to
  // staff (teacher/trainer) rows + the caller's own row only, but we still
  // filter client-side (see views/user/classes) as defense in depth so a
  // roster of other students is never rendered even if the backend filter
  // isn't deployed yet.
  getSessionParticipants: (sessionId: string): Promise<SessionParticipant[]> =>
    api.get(`/sessions/${sessionId}/participants`),
  // STUDENT submits a leave request ("xin nghỉ phép") for a given session.
  createLeaveRequest: (
    sessionId: string,
    reason: string
  ): Promise<LeaveRequestResponse> =>
    api.post(`/sessions/${sessionId}/leave-requests`, { reason }),
  // STUDENT's own submitted leave requests, used to check whether a session
  // already has one before showing the "Xin nghỉ phép" form.
  getMyLeaveRequests: (params?: {
    skip?: number;
    limit?: number;
  }): Promise<PaginatedResponse<LeaveRequestResponse>> => {
    const skip = params?.skip ?? 0;
    const limit = params?.limit ?? 100;
    return api.get(`/users/me/leave-requests?skip=${skip}&limit=${limit}`);
  },
  // STUDENT edits the reason on their own still-pending leave request.
  updateLeaveRequest: (
    requestId: string,
    reason: string
  ): Promise<LeaveRequestResponse> =>
    api.patch(`/users/me/leave-requests/${requestId}`, { reason }),
  // STUDENT withdraws their own still-pending leave request.
  deleteLeaveRequest: (requestId: string): Promise<void> =>
    api.delete(`/users/me/leave-requests/${requestId}`),
};
